import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Edit, GraduationCap, Plus, Trash2 } from "lucide-react";
import { Modal } from "src/components/ui/Modal";
import { FormInput } from "src/components/ui/FormInput";
import { TutorExtendedProfileData, TutorSectionType, TutorEducation } from "src/interfaces/TutorProfile";
import { useTutorProfileEditStore } from "src/store/tutorProfileEditStore";
import { normalizeTutorEducation } from "src/utils/tutorEducation";

interface TutorEducationSectionProps {
  profileData: TutorExtendedProfileData;
  editData: Partial<TutorExtendedProfileData>;
  activeSectionEdit: TutorSectionType | null;
  setActiveSectionEdit: (section: TutorSectionType | null) => void;
  updateEditData: (field: string, value: any) => void;
  updateProfile: (data: Partial<TutorExtendedProfileData>) => Promise<void>;
  isModified?: boolean;
}

const TutorEducationSection: React.FC<TutorEducationSectionProps> = ({
  profileData,
  editData,
  activeSectionEdit,
  setActiveSectionEdit,
  updateEditData,
  updateProfile,
  isModified = false
}) => {
  const {
    editingEducation,
    setEditingEducation,
    addEducation,
    removeEducation,
    updateEducation
  } = useTutorProfileEditStore();
  const education = normalizeTutorEducation(profileData.education);

  // Sync education with updateEditData whenever editingEducation changes
  useEffect(() => {
    if (activeSectionEdit === 'education') {
      updateEditData("education", editingEducation);
    }
  }, [editingEducation, activeSectionEdit, updateEditData]);

  return (
    <>
      {/* Education Section */}
      <Card className={isModified ? "ring-2 ring-blue-200 bg-blue-50/30" : ""}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Education</CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0 rounded-full hover:bg-gray-100"
            onClick={() => {
              const currentEducation = normalizeTutorEducation(editData.education ?? profileData.education);
              setEditingEducation(currentEducation);
              setActiveSectionEdit('education');
            }}
            aria-label="Edit Education"
          >
            <Edit className="h-4 w-4 text-gray-500" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {education.length > 0 ? (
              education.map((edu, index) => (
                <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <GraduationCap className="h-6 w-6 text-blue-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium">{edu.degree}</h3>
                    {edu.level && (
                      <p className="text-sm text-gray-600 font-medium">{edu.level}</p>
                    )}
                    {edu.field && (
                      <p className="text-sm text-gray-600">{edu.field}</p>
                    )}
                    <p className="text-sm text-gray-500">
                      {edu.institution} {edu.year_of_completion && `• ${edu.year_of_completion}`}
                    </p>
                    {edu.country && (
                      <p className="text-sm text-gray-400">{edu.country}</p>
                    )}
                    {edu.gpa && (
                      <p className="text-sm text-gray-400">
                        GPA: {edu.gpa}{edu.gpa_total && `/${edu.gpa_total}`}
                      </p>
                    )}
                    {edu.is_custom_degree && (
                      <p className="text-sm text-blue-600 font-medium">Custom Degree</p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No education information provided</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Education Edit Modal */}
      <Modal
        isOpen={activeSectionEdit === 'education'}
        onClose={() => setActiveSectionEdit(null)}
        title="Edit Education"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Education Details</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addEducation}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Education
            </Button>
          </div>

          {editingEducation.map((edu, index) => (
            <div key={index} className="p-4 border rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Education {index + 1}</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeEducation(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FormInput
                  label="Level"
                  id={`level-${index}`}
                  value={edu.level}
                  onChange={(e) => updateEducation(index, 'level', e.target.value)}
                  placeholder="e.g., Bachelor's, Master's, PhD"
                />

                <FormInput
                  label="Degree"
                  id={`degree-${index}`}
                  value={edu.degree}
                  onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                  placeholder="e.g., Bachelor of Science"
                />

                <FormInput
                  label="Field/Major"
                  id={`field-${index}`}
                  value={edu.field}
                  onChange={(e) => updateEducation(index, 'field', e.target.value)}
                  placeholder="e.g., Computer Science"
                />

                <FormInput
                  label="Institution"
                  id={`institution-${index}`}
                  value={edu.institution}
                  onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                  placeholder="e.g., MIT"
                />

                <FormInput
                  label="Country"
                  id={`country-${index}`}
                  value={edu.country}
                  onChange={(e) => updateEducation(index, 'country', e.target.value)}
                  placeholder="e.g., United States"
                />

                <FormInput
                  label="Year of Completion"
                  id={`year_of_completion-${index}`}
                  value={edu.year_of_completion?.toString() || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateEducation(index, 'year_of_completion', value ? parseInt(value) : 0);
                  }}
                  placeholder="e.g., 2020"
                  type="number"
                />

                <FormInput
                  label="GPA (Optional)"
                  id={`gpa-${index}`}
                  value={edu.gpa?.toString() || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateEducation(index, 'gpa', value ? parseFloat(value) : undefined);
                  }}
                  placeholder="e.g., 3.8"
                  type="number"
                  step="0.1"
                />

                <FormInput
                  label="Total GPA (Optional)"
                  id={`gpa_total-${index}`}
                  value={edu.gpa_total?.toString() || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateEducation(index, 'gpa_total', value ? parseFloat(value) : undefined);
                  }}
                  placeholder="e.g., 4.0"
                  type="number"
                  step="0.1"
                />
              </div>

              {/* Custom Degree Checkbox */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id={`is_custom_degree-${index}`}
                  checked={edu.is_custom_degree || false}
                  onChange={(e) => updateEducation(index, 'is_custom_degree', e.target.checked)}
                  className="rounded"
                />
                <label htmlFor={`is_custom_degree-${index}`} className="text-sm">
                  This is a custom/non-standard degree
                </label>
              </div>
            </div>
          ))}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setActiveSectionEdit(null)}>
              Cancel
            </Button>
            <Button onClick={() => {
              updateProfile(editData);
              setActiveSectionEdit(null);
            }}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TutorEducationSection;
